import {
  formatTooltipAxisHeader,
  formatTooltipSeriesValue,
} from "./displayUnits/displayFormatters";

type AxisConfig = {
  name: string;
  gridIndex?: number;
  min?: number;
  max?: number;
  interval?: number;
  splitNumber?: number;
  silent?: boolean;
  axisLabel?: Record<string, any>;
  minorTick?: Record<string, any>;
  minorSplitLine?: Record<string, any>;
  splitLine?: Record<string, any>;
  axisTick?: Record<string, any>;
  axisLine?: Record<string, any>;
  nameTextStyle?: Record<string, any>;
  [key: string]: any;
};

export const ndBoardTheme = {
  background: "#fbfcfd",
  gridBackground: "#ffffff",
  axis: "#39434d",
  axisText: "#28323c",
  axisMuted: "#5f6a76",
  majorGrid: "#d6dde5",
  minorGrid: "#eceff3",
  border: "#39434d",
  referenceCurve: "#b6c0ca",
  activeCurve: "#0aa1ad",
  supportCurve: "#39434d",
  auxiliaryCurve: "#202833",
  marker: "#1890ff",
  markerCore: "#ffffff",
  infoBorder: "#6f8192",
  infoFill: "rgba(255,255,255,0.96)",
};

type BoardPanelPlacement = "topRight" | "bottomRight";

export const createBoardPanelAnchor = (
  placement: BoardPanelPlacement,
  panelWidth: number,
  panelHeight: number
) => {
  if (placement === "topRight") {
    return {
      x: ({ width }: { width: number; height: number }) =>
        Math.max(24, Math.round(width - panelWidth - 92)),
      y: () => 28,
    };
  }

  return {
    x: ({ width }: { width: number; height: number }) =>
      Math.max(24, Math.round(width - panelWidth - 78)),
    y: ({ height }: { width: number; height: number }) =>
      Math.max(24, Math.round(height - panelHeight - 108)),
  };
};

export const createBoardGrid = (overrides: Record<string, any> = {}): any => ({
  show: true,
  backgroundColor: ndBoardTheme.gridBackground,
  borderColor: ndBoardTheme.border,
  borderWidth: 1.1,
  containLabel: false,
  ...overrides,
});

export const createBoardValueAxis = ({
  axisLabel,
  minorTick,
  minorSplitLine,
  splitLine,
  axisTick,
  axisLine,
  nameTextStyle,
  ...rest
}: AxisConfig): any => ({
  type: "value",
  nameLocation: "end",
  nameGap: 10,
  axisLine: {
    show: true,
    lineStyle: {
      color: ndBoardTheme.axis,
      width: 1,
    },
    ...axisLine,
  },
  axisTick: {
    show: true,
    length: 7,
    lineStyle: {
      color: ndBoardTheme.axis,
      width: 1,
    },
    ...axisTick,
  },
  splitLine: {
    show: true,
    lineStyle: {
      color: ndBoardTheme.majorGrid,
      width: 0.9,
    },
    ...splitLine,
  },
  minorTick: {
    show: true,
    splitNumber: 5,
    length: 3,
    lineStyle: {
      color: ndBoardTheme.axisMuted,
      width: 0.6,
    },
    ...minorTick,
  },
  minorSplitLine: {
    show: true,
    lineStyle: {
      color: ndBoardTheme.minorGrid,
      width: 0.6,
    },
    ...minorSplitLine,
  },
  axisLabel: {
    color: ndBoardTheme.axisText,
    fontSize: 11,
    margin: 8,
    ...axisLabel,
  },
  nameTextStyle: {
    color: ndBoardTheme.axisText,
    fontSize: 11,
    fontWeight: 500,
    padding: [0, 0, 6, 0],
    ...nameTextStyle,
  },
  ...rest,
});

export const createBoardTooltip = (): any => ({
  trigger: "axis",
  backgroundColor: "rgba(255,255,255,0.98)",
  borderColor: ndBoardTheme.border,
  borderWidth: 1,
  textStyle: {
    color: ndBoardTheme.axisText,
    fontSize: 11,
  },
  extraCssText: "border-radius:0;box-shadow:none;padding:8px 10px;",
  formatter: (params: any) => {
    const items = Array.isArray(params) ? params : [params];
    const axisValue = items[0]?.axisValue;
    const lines = [formatTooltipAxisHeader(axisValue)];

    items.forEach((item) => {
      if (!item?.seriesName || item.seriesType === "custom") {
        return;
      }

      lines.push(
        `${item.marker}${item.seriesName}&nbsp;&nbsp;<b>${formatTooltipSeriesValue(
          item.seriesName,
          item.value
        )}</b>`
      );
    });

    return lines.join("<br/>");
  },
  axisPointer: {
    type: "cross",
    lineStyle: {
      color: ndBoardTheme.axisMuted,
      width: 1,
      type: "dashed",
    },
    label: {
      backgroundColor: ndBoardTheme.axisMuted,
      borderRadius: 0,
      color: "#ffffff",
      padding: [4, 6],
    },
  },
});

const boardSaveAsImageIcon =
  "path://M18 20H6v-2h12v2ZM12 16l-5-5h3V4h4v7h3l-5 5Z";

export const createBoardToolbox = (name: string, onSaveAsImage?: () => void): any => ({
  right: 4,
  top: 4,
  feature: {
    [onSaveAsImage ? "mySaveAsImage" : "saveAsImage"]: {
      name,
      title: "Save as image",
      icon: onSaveAsImage ? boardSaveAsImageIcon : undefined,
      onclick: onSaveAsImage,
      pixelRatio: 2,
      iconStyle: {
        borderColor: ndBoardTheme.axis,
      },
      emphasis: {
        iconStyle: {
          borderColor: ndBoardTheme.activeCurve,
        },
      },
    },
  },
});

export const boardCurveLabelStyle = {
  color: ndBoardTheme.axisText,
  fontSize: 11,
  fontWeight: 500,
  backgroundColor: "rgba(255,255,255,0.72)",
  padding: [1, 3],
};

export const boardInfoTextStyle = {
  fill: ndBoardTheme.axisText,
  fontSize: 11,
  fontWeight: 400,
  lineHeight: 18,
};

export const boardInfoPanelStyle = {
  fill: ndBoardTheme.infoFill,
  stroke: ndBoardTheme.infoBorder,
  lineWidth: 1,
};

export const boardTypeLabelStyle = {
  fill: ndBoardTheme.axisText,
  fontSize: 13,
  fontWeight: 600,
};

export const boardMarkerStyles = {
  outer: {
    fill: ndBoardTheme.marker,
  },
  inner: {
    fill: ndBoardTheme.markerCore,
  },
  core: {
    fill: ndBoardTheme.marker,
  },
  ring: {
    fill: ndBoardTheme.markerCore,
    stroke: ndBoardTheme.marker,
    lineWidth: 1.6,
  },
};
